import React, { useState, useEffect, useCallback } from 'react';
import { fmt, api } from '../utils/api';

export default function RecentTransactions({ transactions, onRefresh, readOnly = false, title = '🧾 Recent Transactions', forceManualOnly = false, onViewAll, collapsible = false, defaultCollapsed = false }) {
  const [view, setView] = useState(forceManualOnly ? 'manual' : 'recent'); // 'recent' | 'manual'
  const [panelCollapsed, setPanelCollapsed] = useState(collapsible && defaultCollapsed);
  const [manualList, setManualList] = useState(null);
  const [loadingManual, setLoadingManual] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    description: '',
    amount: '',
    date: '',
    category: '',
    isIncome: false,
  });

  const loadManual = useCallback(async () => {
    setLoadingManual(true);
    try {
      const data = await api.getTransactionsList({ isManual: true, limit: 100 });
      setManualList(data.transactions || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingManual(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'manual' && manualList === null) loadManual();
  }, [view, manualList, loadManual]);

  const list = view === 'manual' ? (manualList || []) : (transactions || []);
  const shown = expanded ? list : list.slice(0, 8);

  const remove = async (id) => {
    if (!window.confirm('Delete this manual entry?')) return;
    setBusyId(id);
    try {
      await api.deleteTransaction(id);
      if (view === 'manual') loadManual();
      onRefresh?.();
    } catch (err) {
      console.error(err);
    } finally {
      setBusyId(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.description.trim() || !form.amount || !form.date) {
      setError('Description, amount, and date are required.');
      return;
    }

    setSubmitting(true);
    try {
      await api.addManualTransaction({
        description: form.description.trim(),
        amount: parseFloat(form.amount),
        date: form.date,
        category: form.category.trim() || undefined,
        isIncome: form.isIncome,
      });
      setForm({ description: '', amount: '', date: '', category: '', isIncome: false });
      setShowForm(false);
      if (view === 'manual') loadManual();
      onRefresh?.();
    } catch (err) {
      setError('Failed to add. Please try again.');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        {collapsible ? (
          <button style={styles.collapsibleTitleBtn} onClick={() => setPanelCollapsed((c) => !c)}>
            <span style={styles.chevron}>{panelCollapsed ? '▸' : '▾'}</span>
            <h2 style={{ ...styles.title, margin: 0 }}>{title}</h2>
          </button>
        ) : (
          <h2 style={styles.title}>{title}</h2>
        )}
        <div style={styles.headerRight}>
          {readOnly ? (
            list.length > 8 && (
              <button style={styles.viewAllLink} onClick={onViewAll}>
                View all in Transactions →
              </button>
            )
          ) : (
            list.length > 0 && (
              <span style={styles.count}>
                {shown.length < list.length ? `${shown.length} of ${list.length} shown` : `${list.length} shown`}
              </span>
            )
          )}
          {!readOnly && (
            <button style={styles.addBtn} onClick={(e) => { e.stopPropagation(); setShowForm((s) => !s); }}>
              {showForm ? 'Cancel' : '+ Add Manual'}
            </button>
          )}
        </div>
      </div>

      {!panelCollapsed && (
      <>
      {!readOnly && !forceManualOnly && (
      <div style={styles.viewTabs}>
        <button
          style={{ ...styles.viewTab, ...(view === 'recent' ? styles.viewTabActive : {}) }}
          onClick={() => setView('recent')}
        >
          Recent
        </button>
        <button
          style={{ ...styles.viewTab, ...(view === 'manual' ? styles.viewTabActive : {}) }}
          onClick={() => setView('manual')}
        >
          Manual Entries
        </button>
      </div>
      )}

      {view === 'manual' && loadingManual && (
        <p style={styles.empty}>Loading manual entries...</p>
      )}

      {!readOnly && showForm && (
        <form style={styles.form} onSubmit={submit}>
          <input
            style={styles.input}
            placeholder="Description (e.g. Cash - Farmers Market)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <div style={styles.formRow}>
            <input
              style={styles.input}
              type="number"
              step="0.01"
              placeholder="Amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
            />
            <input
              style={styles.input}
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </div>
          <div style={styles.formRow}>
            <input
              style={styles.input}
              placeholder="Category (optional)"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
            />
            <label style={styles.checkboxLabel}>
              <input
                type="checkbox"
                checked={form.isIncome}
                onChange={(e) => setForm({ ...form, isIncome: e.target.checked })}
              />
              This is income
            </label>
          </div>
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.submitBtn} type="submit" disabled={submitting}>
            {submitting ? 'Adding...' : 'Add Transaction'}
          </button>
        </form>
      )}

      {list.length === 0 && !(view === 'manual' && loadingManual) ? (
        <p style={styles.empty}>
          {view === 'manual'
            ? "No manual entries yet. Use \"+ Add Manual\" above for cash spending or an account you haven't linked."
            : "No transactions yet. Connect an account, or add a manual entry for cash spending or an account you haven't linked."}
        </p>
      ) : (
        <>
          <div style={styles.list}>
            {shown.map((txn) => (
              <TransactionRow
                key={txn.id}
                txn={txn}
                busy={busyId === txn.id}
                onDelete={() => remove(txn.id)}
                readOnly={readOnly}
              />
            ))}
          </div>

          {!readOnly && list.length > 8 && (
            <button style={styles.showMore} onClick={() => setExpanded(!expanded)}>
              {expanded ? 'Show less' : `Show all ${list.length} transactions`}
            </button>
          )}
        </>
      )}
      </>
      )}
    </div>
  );
}

function TransactionRow({ txn, busy, onDelete, readOnly }) {
  const name = txn.merchant_name || txn.description || 'Unknown';
  const cat = txn.category?.[0] || '';

  return (
    <div style={styles.row}>
      <div style={styles.left}>
        <div style={styles.name}>
          {name}
          {txn.is_manual && <span style={styles.manualTag}>Manual</span>}
        </div>
        <div style={styles.meta}>
          <span style={styles.date}>{fmt.shortDate(txn.date)}</span>
          {cat && <span style={styles.cat}>{cat}</span>}
          {txn.account_name && (
            <span style={styles.account}>
              {txn.institution_name ? `${txn.institution_name} ${txn.account_name}` : txn.account_name}
            </span>
          )}
        </div>
      </div>
      <div style={styles.right}>
        <div style={{
          ...styles.amount,
          color: txn.amount < 0 ? '#00d4aa' : '#e5e7eb',
        }}>
          {txn.amount < 0 ? '+' : ''}{fmt.currency(Math.abs(txn.amount))}
        </div>
        {!readOnly && txn.is_manual && (
          <button style={styles.deleteBtn} onClick={onDelete} disabled={busy}>
            ✕
          </button>
        )}
      </div>
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    flexWrap: 'wrap',
    gap: '0.5rem',
  },
  collapsibleTitleBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  },
  chevron: {
    color: '#6b7280',
    fontSize: '0.8rem',
  },
  headerRight: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  viewTabs: { display: 'flex', gap: '0.4rem', marginBottom: '1rem' },
  viewTab: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '999px',
    padding: '4px 12px',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  viewTabActive: {
    background: 'rgba(0, 212, 170, 0.12)',
    borderColor: '#00d4aa',
    color: '#00d4aa',
  },
  title: {
    margin: 0,
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  count: {
    color: '#6b7280',
    fontSize: '0.75rem',
    fontFamily: "'DM Mono', monospace",
  },
  viewAllLink: {
    background: 'transparent',
    border: 'none',
    color: '#00d4aa',
    fontSize: '0.75rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
    padding: 0,
  },
  addBtn: {
    background: 'transparent',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '0.75rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    marginBottom: '1rem',
    padding: '1rem',
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
  },
  formRow: { display: 'flex', gap: '0.5rem', alignItems: 'center' },
  input: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '6px',
    padding: '0.5rem 0.6rem',
    color: '#fff',
    fontSize: '0.85rem',
    fontFamily: "'Syne', sans-serif",
    flex: 1,
    width: '100%',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    color: '#9ca3af',
    fontSize: '0.75rem',
    fontFamily: "'Syne', sans-serif",
    whiteSpace: 'nowrap',
  },
  error: {
    color: '#ef4444',
    fontSize: '0.75rem',
    margin: 0,
  },
  submitBtn: {
    background: '#00d4aa',
    color: '#0a0f1e',
    border: 'none',
    borderRadius: '6px',
    padding: '0.6rem',
    fontSize: '0.8rem',
    fontWeight: '700',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  empty: {
    color: '#4b5563',
    fontSize: '0.875rem',
    textAlign: 'center',
    padding: '1rem 0',
    margin: 0,
  },
  list: {},
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0',
    borderBottom: '1px solid #1e2a3a',
  },
  left: { flex: 1, minWidth: 0 },
  name: {
    color: '#e5e7eb',
    fontSize: '0.875rem',
    fontWeight: '500',
    fontFamily: "'Syne', sans-serif",
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  manualTag: {
    fontSize: '0.6rem',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '4px',
    padding: '1px 5px',
    fontFamily: "'Syne', sans-serif",
    flexShrink: 0,
  },
  meta: {
    display: 'flex',
    gap: '0.4rem',
    alignItems: 'center',
    marginTop: '2px',
    flexWrap: 'wrap',
  },
  date: {
    color: '#4b5563',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  cat: {
    color: '#374151',
    fontSize: '0.65rem',
    background: '#1e2a3a',
    padding: '1px 6px',
    borderRadius: '4px',
    fontFamily: "'Syne', sans-serif",
  },
  account: {
    color: '#374151',
    fontSize: '0.65rem',
    fontFamily: "'DM Mono', monospace",
  },
  right: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 },
  amount: {
    fontSize: '0.9rem',
    fontWeight: '600',
    fontFamily: "'DM Mono', monospace",
    marginLeft: '1rem',
  },
  deleteBtn: {
    background: 'transparent',
    border: 'none',
    color: '#4b5563',
    cursor: 'pointer',
    fontSize: '0.85rem',
    padding: '0 2px',
  },
  showMore: {
    background: 'transparent',
    border: '1px solid #1e2a3a',
    color: '#6b7280',
    borderRadius: '8px',
    padding: '8px',
    width: '100%',
    cursor: 'pointer',
    fontSize: '0.8rem',
    marginTop: '0.75rem',
    fontFamily: "'Syne', sans-serif",
  },
};
